/**
 * Employee Table Component
 * Displays employee data in a sortable table with selection support
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { 
  Eye, Edit2, Trash2, RotateCcw, KeyRound, 
  Users, AlertTriangle, ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';

// Sortable columns configuration
const SORTABLE_COLUMNS = [
  { id: 'login_id', label: 'Login ID', icon: KeyRound },
  { id: 'department', label: 'Department' },
  { id: 'designation', label: 'Designation' },
  { id: 'grade', label: 'Grade' },
  { id: 'status', label: 'Status' },
  { id: 'auth_status', label: 'Auth Status' },
];

const getStatusBadge = (status) => {
  const styles = {
    active: 'bg-emerald-100 text-emerald-700',
    inactive: 'bg-slate-100 text-slate-700',
    suspended: 'bg-red-100 text-red-700',
    retired: 'bg-blue-100 text-blue-700'
  };
  return styles[status] || 'bg-slate-100 text-slate-700';
};

const getAuthBadge = (status) => {
  const styles = {
    pending: 'bg-amber-100 text-amber-700',
    authorized: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700'
  };
  return styles[status] || 'bg-slate-100 text-slate-700';
};

export const EmployeeTable = ({
  employees,
  loading,
  selectionMode,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onDelete,
  onRestore,
  activeTab,
  canSeeLoginId,
  isAdmin,
  getDepartmentName,
  sortBy,
  sortOrder,
  onSort,
  tabLabel
}) => {
  const navigate = useNavigate();
  const allSelected = employees.length > 0 && selectedIds.size === employees.length;

  // Get sort icon for a column
  const getSortIcon = (columnId) => {
    if (sortBy !== columnId) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-3.5 h-3.5 text-blue-600" />
      : <ArrowDown className="w-3.5 h-3.5 text-blue-600" />;
  };

  // Calculate colspan based on visible columns
  const getColSpan = () => {
    let cols = 7; // Base columns: Employee, Department, Designation, Grade, Status, Auth Status, Actions
    if (selectionMode) cols += 1;
    if (canSeeLoginId) cols += 1;
    return cols;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th colSpan={getColSpan()} className="px-4 py-12 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                </th>
              </tr>
            </thead>
          </table>
        </div>
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th colSpan={getColSpan()} className="px-4 py-12 text-center text-slate-500">
                  <div className="flex flex-col items-center gap-2">
                    <Users className="w-12 h-12 text-slate-300" />
                    <p>No employees found for "{tabLabel}"</p>
                  </div>
                </th>
              </tr>
            </thead>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="employee-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              {/* Checkbox column for bulk selection */}
              {selectionMode && (
                <th className="px-4 py-3 w-12">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={onToggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left font-medium text-slate-600">Employee</th>
              {canSeeLoginId && (
                <th 
                  className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors group"
                  onClick={() => onSort('login_id')}
                  data-testid="sort-login_id"
                >
                  <div className="flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5" />
                    Login ID
                    {getSortIcon('login_id')}
                  </div>
                </th>
              )}
              <th 
                className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors group"
                onClick={() => onSort('department')}
                data-testid="sort-department"
              >
                <div className="flex items-center gap-1.5">
                  Department
                  {getSortIcon('department')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors group"
                onClick={() => onSort('designation')}
                data-testid="sort-designation"
              >
                <div className="flex items-center gap-1.5">
                  Designation
                  {getSortIcon('designation')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors group"
                onClick={() => onSort('grade')}
                data-testid="sort-grade"
              >
                <div className="flex items-center gap-1.5">
                  Grade
                  {getSortIcon('grade')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors group"
                onClick={() => onSort('status')}
                data-testid="sort-status"
              >
                <div className="flex items-center gap-1.5">
                  Status
                  {getSortIcon('status')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors group"
                onClick={() => onSort('auth_status')}
                data-testid="sort-auth_status"
              >
                <div className="flex items-center gap-1.5">
                  Auth Status
                  {getSortIcon('auth_status')}
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {employees.map((emp) => (
              <tr 
                key={emp.id} 
                className={`hover:bg-slate-50 ${emp.profile_incomplete ? 'bg-amber-50/50' : ''} ${selectedIds.has(emp.id) ? 'bg-blue-50' : ''}`} 
                data-testid={`emp-row-${emp.id}`}
              >
                {/* Checkbox for selection */}
                {selectionMode && (
                  <td className="px-4 py-3 w-12">
                    <Checkbox
                      checked={selectedIds.has(emp.id)}
                      onCheckedChange={() => onToggleSelect(emp.id)}
                      data-testid={`select-${emp.id}`}
                    />
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                      emp.profile_incomplete 
                        ? 'bg-gradient-to-br from-amber-500 to-orange-600' 
                        : 'bg-gradient-to-br from-blue-500 to-purple-600'
                    }`}>
                      {emp.name?.charAt(0) || emp.first_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{emp.name || `${emp.first_name} ${emp.last_name}`}</p>
                        {emp.profile_incomplete && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" title={`Missing: ${emp.missing_fields?.join(', ')}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Incomplete
                          </span>
                        )}
                        {emp.is_on_probation && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            Probation
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">{emp.employee_code} • {emp.email?.includes('@pending.local') ? <span className="text-amber-600">Email pending</span> : emp.email}</p>
                    </div>
                  </div>
                </td>
                {canSeeLoginId && (
                  <td className="px-4 py-3">
                    {emp.login_id ? (
                      <span className="inline-flex items-center px-2 py-1 bg-slate-100 rounded font-mono text-xs text-slate-700">
                        <KeyRound className="w-3 h-3 mr-1.5 text-slate-400" />
                        {emp.login_id}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-xs">Not assigned</span>
                    )}
                  </td>
                )}
                <td className="px-4 py-3 text-slate-600">
                  {getDepartmentName(emp.department_id)}
                </td>
                <td className="px-4 py-3 text-slate-600">{emp.designation || '-'}</td>
                <td className="px-4 py-3 text-slate-600">{emp.grade || '-'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(emp.status)}`}>
                    {emp.status?.replace('_', ' ').toUpperCase() || 'ACTIVE'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAuthBadge(emp.authorization_status)}`}>
                    {emp.authorization_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/employee-master/${emp.id}`)}
                      data-testid={`view-${emp.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {emp.status !== 'deleted' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/employee-master/${emp.id}/edit`)}
                        data-testid={`edit-${emp.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    )}
                    {isAdmin && emp.status !== 'deleted' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => onDelete(emp)}
                        data-testid={`delete-${emp.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {isAdmin && emp.status === 'deleted' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => onRestore(emp)}
                        data-testid={`restore-${emp.id}`}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeTable;
